import { create } from 'zustand';
import type { RejectionCategory } from '@/features/dashboard/api/dashboard.schemas';

/**
 * QuickSight-style cross-filter store for the Identity Proofing dashboard.
 *
 * When the user clicks a segment of a chart (rejection-category donut,
 * top devices list, browser bar, status panel, …) the corresponding
 * filter is set in this store. `useDashboardData` reads the store and
 * appends the values as query params to the `/verifications/metrics`
 * call — so the entire dashboard re-aggregates against the filtered
 * subset on each click.
 *
 * The store is intentionally **page-local in mental model** (not
 * persisted, not URL-synced, not shared with other pages) — opening a
 * different page resets nothing because the store is not auto-cleared;
 * we wipe it on `DashboardPage` mount/unmount via `useEffect` to match
 * the "filters live with the dashboard view" UX. See
 * `pages/DashboardPage.tsx`.
 *
 * v1 semantics: each dimension holds at most ONE value (toggleable).
 * Clicking the same value twice clears it. Multi-select is a follow-up
 * if real usage demands it.
 */
export interface DashboardFilters {
  /** Device name as it appears in the Top Devices widget. */
  device?: string;
  browser?: string;
  rejectionCategory?: RejectionCategory;
  /** Date bucket key from `validationsByDate.date[]` — same format the server emits. */
  dateBucket?: string;
  /** Coarse status from the PanelGroup KPI cards. */
  status?: 'approved' | 'rejected' | 'pending';
  /** ISO-3166 alpha-3 country code from the Map widget. */
  country?: string;
  /**
   * Composite "<PLATFORM>|<version>" key from the SDK Version widget.
   * Sent verbatim to the server, which exact-matches it against rows
   * normalized the same way (uppercase platform, skip Unknown).
   */
  sdkVersion?: string;
  /**
   * Document type identifier from the Document Type widget
   * (e.g. "ARG_3", "ARG_4", "COL_3"). Sent verbatim; server exact-matches
   * against the raw `idType` attribute on each verification row.
   */
  idType?: string;
}

type FilterKey = keyof DashboardFilters;

interface DashboardFiltersState {
  filters: DashboardFilters;
  /** Toggle: if the value is already set on `key`, clear it; else set. */
  toggleFilter: <K extends FilterKey>(key: K, value: DashboardFilters[K]) => void;
  clearFilter: (key: FilterKey) => void;
  clearAll: () => void;
}

export const useDashboardFiltersStore = create<DashboardFiltersState>((set) => ({
  filters: {},
  toggleFilter: (key, value) =>
    set((state) => {
      const current = state.filters[key];
      const next = { ...state.filters };
      if (current === value) {
        delete next[key];
      } else {
        // Cast through unknown — TS can't track the per-key type across
        // the dynamic-key write, but the public API enforces it.
        (next as Record<string, unknown>)[key] = value;
      }
      return { filters: next };
    }),
  clearFilter: (key) =>
    set((state) => {
      const next = { ...state.filters };
      delete next[key];
      return { filters: next };
    }),
  clearAll: () => set({ filters: {} }),
}));

/** Helper for components that just need to know if anything is active. */
export function hasActiveFilters(filters: DashboardFilters): boolean {
  return Object.keys(filters).length > 0;
}
